/**
 * Product Service - Backend API integration for products
 * 
 * Endpoints:
 * GET    /api/products              - List all products (with filters)
 * GET    /api/products/:id          - Get single product
 * POST   /api/products              - Create product (admin)
 * PATCH  /api/products/:id          - Update product (admin)
 * PATCH  /api/products/:id/toggle-stock - Toggle availability (admin)
 * DELETE /api/products/:id          - Delete product (admin)
 */

import api from './api';

const productService = {
  /**
   * Get all products with optional filters
   * @param {Object} filters - { category, categoryId, available, search }
   */
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.category) params.append('category', filters.category);
    if (filters.categoryId) params.append('categoryId', filters.categoryId);
    if (filters.available !== undefined) params.append('available', filters.available);
    if (filters.search) params.append('search', filters.search);
    
    const queryString = params.toString();
    const url = queryString ? `/products?${queryString}` : '/products';
    
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get single product by ID
   */
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  /**
   * Create a new product (admin only)
   * @param {Object} productData - { name, description, categoryId, pricePerUnit, unitType, stockQuantity, isAvailable }
   */
  create: async (productData) => {
    const response = await api.post('/products', {
      name: productData.name,
      description: productData.description,
      categoryId: productData.categoryId,
      pricePerUnit: parseFloat(productData.pricePerUnit || productData.price),
      unitType: productData.unitType || 'kg',
      stockQuantity: parseInt(productData.stockQuantity) || 0,
      shelfLifeDays: parseInt(productData.shelfLifeDays) || 3,
      isAvailable: productData.isAvailable !== false,
      keywords: productData.keywords ?? null,
      hsnCode: productData.hsnCode || null,
      taxMasterId: productData.taxMasterId ?? null,
    });
    return response.data;
  },

  /**
   * Update a product (admin only)
   */
  update: async (id, updates) => {
    const payload = {};
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.description !== undefined) payload.description = updates.description;
    if (updates.categoryId !== undefined) payload.categoryId = updates.categoryId;
    if (updates.pricePerUnit !== undefined || updates.price !== undefined) {
      payload.pricePerUnit = parseFloat(updates.pricePerUnit || updates.price);
    }
    if (updates.unitType !== undefined) payload.unitType = updates.unitType;
    if (updates.isAvailable !== undefined) payload.isAvailable = updates.isAvailable;
    if (updates.shelfLifeDays !== undefined) payload.shelfLifeDays = parseInt(updates.shelfLifeDays);
    if (updates.imageUrl !== undefined || updates.image !== undefined) {
      payload.image = updates.imageUrl || updates.image;
    }
    if (updates.keywords !== undefined) payload.keywords = updates.keywords;
    if (updates.hsnCode !== undefined) payload.hsnCode = updates.hsnCode;
    if (updates.taxMasterId !== undefined) payload.taxMasterId = updates.taxMasterId;

    const response = await api.patch(`/products/${id}`, payload);
    return response.data;
  },

  /**
   * Search active Goods (GOODS) tax_master records for the product Tax Code
   * picker (BMF-137). Matches tax_code, description or gst_percentage.
   * @returns {Array} [{ id, taxCode, description, gstPercentage, ... }]
   */
  searchTaxMaster: async (search = '') => {
    // Only send `search` when there's an actual term. An empty query still runs
    // (it loads the default active-GOODS list when the picker opens), but we omit
    // the meaningless `search=` param rather than sending it blank.
    const searchTerm = search.trim();
    const params = { taxType: 'GOODS', isActive: 'true', limit: 20 };
    if (searchTerm) params.search = searchTerm;
    const response = await api.get('/tax-master', { params });
    return response.data?.data || [];
  },

  /**
   * Get ALL products including hidden ones (admin only)
   */
  getAllAdmin: async (params = {}) => {
    const response = await api.get('/admin/products', { params });
    return response.data;
  },

  /**
   * Toggle product visibility (hide/show) — admin only
   */
  toggleVisibility: async (id) => {
    const response = await api.patch(`/admin/products/${id}/toggle-visibility`);
    return response.data;
  },

  /**
   * Toggle product availability (admin only)
   */
  toggleStock: async (id) => {
    const response = await api.patch(`/products/${id}/toggle-stock`);
    return response.data;
  },

  /**
   * Delete a product (admin only)
   */
  delete: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  /**
   * Update stock quantity (admin only)
   */
  updateStock: async (id, quantity) => {
    const response = await api.patch(`/products/${id}`, {
      stockQuantity: parseInt(quantity)
    });
    return response.data;
  },

  /**
   * Add image to product via URL (admin only)
   */
  addImage: async (productId, imageUrl, isPrimary = false) => {
    const response = await api.post(`/products/${productId}/images`, { imageUrl, isPrimary });
    return response.data;
  },

  uploadImage: async (productId, file) => {
    const form = new FormData();
    form.append('image', file);
    const response = await api.post(`/products/${productId}/images/upload`, form);
    return response.data;
  },

  /**
   * Set primary image (admin only)
   */
  setPrimaryImage: async (productId, imageId) => {
    const response = await api.patch(`/products/${productId}/images/${imageId}/primary`);
    return response.data;
  },

  /**
   * Remove image from product (admin only)
   */
  removeImage: async (productId, imageId) => {
    const response = await api.delete(`/products/${productId}/images/${imageId}`);
    return response.data;
  },

  // ── Variant methods (admin only) ──────────────────────────────────────────

  createVariant: async (productId, { weight, unit, pricePerUnit, stockQuantity, isAvailable = true, sortOrder }) => {
    const payload = {
      weight: parseFloat(weight),
      unit,
      stockQuantity: parseInt(stockQuantity) || 0,
      isAvailable,
    };
    // pricePerUnit is optional — backend auto-derives when omitted.
    if (pricePerUnit !== undefined && pricePerUnit !== '' && pricePerUnit !== null) {
      payload.pricePerUnit = parseFloat(pricePerUnit);
    }
    if (sortOrder !== undefined) payload.sortOrder = sortOrder;
    const response = await api.post(`/products/${productId}/variants`, payload);
    return response.data;
  },

  updateVariant: async (productId, variantId, updates) => {
    const payload = {};
    if (updates.weight !== undefined) payload.weight = parseFloat(updates.weight);
    if (updates.unit !== undefined) payload.unit = updates.unit;
    if (updates.pricePerUnit !== undefined) payload.pricePerUnit = parseFloat(updates.pricePerUnit);
    if (updates.stockQuantity !== undefined) payload.stockQuantity = parseInt(updates.stockQuantity);
    if (updates.isAvailable !== undefined) payload.isAvailable = updates.isAvailable;
    if (updates.sortOrder !== undefined) payload.sortOrder = updates.sortOrder;
    const response = await api.patch(`/products/${productId}/variants/${variantId}`, payload);
    return response.data;
  },

  deleteVariant: async (productId, variantId) => {
    await api.delete(`/products/${productId}/variants/${variantId}`);
  },
};

export default productService;